// frontend/src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import QuotaLimitModal from './components/QuotaLimitModal';

// Importación de las páginas modulares
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Register } from './pages/Register';
import { Navbar } from './components/Navbar';
import { SetPassword } from './pages/SetPassword';
import { ConfirmDeletion } from './pages/ConfirmDeletion';
import { Download } from './pages/Download';
import { Projects } from './pages/Projects';
import { ProjectDetail } from './pages/ProjectDetail';
import { Team } from './pages/Team';
import { ClientPage } from './pages/ClientPage';
import { AlertsPage } from './pages/AlertsPage';
import { HelpPage } from './pages/HelpPage';
import { CookieBanner } from './components/CookieBanner';
import { ScriptGenerator } from './pages/ScriptGenerator';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { CookiePolicy } from './pages/CookiePolicy';
import { TermsOfService } from './pages/TermsOfService';

// 🌟 NUEVO: Importamos el contenedor principal del Wizard
import Migration from './pages/Migration';

// Layout Global para inyectar el Navbar solo donde corresponde
const Layout = ({ children, user, handleLogout }) => {
  const location = useLocation();
  // El Navbar solo se mostrará en estas 3 rutas
  // const showNavbar = ['/', '/register', '/login'].includes(location.pathname);
  const showNavbar = ['/register', '/login'].includes(location.pathname);


  return (
    <div className="font-sans text-slate-900 selection:bg-blue-100 min-h-screen bg-[#F8FAFC]">
      {showNavbar && <Navbar user={user} handleLogout={handleLogout} />}
      {children}
      <CookieBanner />
    </div>
  );
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [quotaError, setQuotaError] = useState(null);

  useEffect(() => {
    // Verificamos si existe una sesión activa al cargar
    const checkSession = () => {
      const token = localStorage.getItem('vnext_auth_token');
      const savedUser = localStorage.getItem('vnext_user');

      if (token && savedUser) {
        setUser(JSON.parse(savedUser));
      }
      setIsLoading(false);
    };
    checkSession();
  }, []);

  // ── Listener global para errores de quota/plan ──
  useEffect(() => {
    const handler = (e) => {
      setQuotaError({ code: e.detail.code, params: e.detail.params });
    };
    window.addEventListener('datiaflow:quota-error', handler);
    return () => window.removeEventListener('datiaflow:quota-error', handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('vnext_auth_token');
    localStorage.removeItem('vnext_user');
    setUser(null);
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
      </div>
    );
  }

  // Enrutador Principal (react-router-dom)
  return (
    <Router>
      <Layout user={user} handleLogout={handleLogout}>
        <Routes>
          {/* Rutas Públicas */}
          <Route path="/" element={<Landing />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login setUser={setUser} />} />

          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/confirm-deletion" element={<ConfirmDeletion />} />

          {/* Rutas Públicas — Legal */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* Rutas Protegidas: Si no hay usuario, te expulsa al /login */}
          <Route
            path="/dashboard"
            element={user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/login" replace />}
          />

          <Route
            path="/download/:uuid"
            element={user ? <Download user={user} /> : <Navigate to="/login" replace />}
          />

          {/* 🌟 NUEVA RUTA PROTEGIDA: El Wizard de ETL */}
          <Route
            path="/migracion"
            element={user ? <Migration /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/projects"
            element={user ? <Projects user={user} setUser={setUser} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/projects/:idProject"
            element={user ? <ProjectDetail user={user} setUser={setUser} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/team"
            element={user ? <Team user={user} setUser={setUser} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/client"
            element={<ClientPage user={user} setUser={setUser} />}
          />
          <Route
            path="/alerts"
            element={user ? <AlertsPage user={user} setUser={setUser} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/help"
            element={<HelpPage user={user} setUser={setUser} />}
          />
          {/* <Route path="/scripts" element={
            user && Number(user.role) === 1
              ? <ScriptGenerator user={user} setUser={setUser} />
              : <Navigate to={user ? "/dashboard" : "/login"} replace />
          } /> */}
          <Route path="/scripts" element={
            user && user.scriptVisible
              ? <ScriptGenerator user={user} setUser={setUser} />
              : <Navigate to={user ? "/dashboard" : "/login"} replace />
          } />
          {/* Ruta Comodín: Cualquier URL extraña te regresa al inicio */}
          <Route
            path="*"
            element={<Navigate to="/" replace />}
          />
        </Routes>
      </Layout>

      {/* Modal global de límite de plan */}
      <QuotaLimitModal
        visible={!!quotaError}
        onClose={() => setQuotaError(null)}
        errorCode={quotaError?.code}
        params={quotaError?.params}
      />
    </Router>
  );
};

export default App;